import React, { useEffect, useState, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  FileDownload,
  FileUpload,
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { FormNavigationProvider, NavTextField, NavSelect } from '../components/FormNavigation';
import { useAppDispatch } from '../store';
import { showNotification } from '../store/notificationSlice';
import { apiService } from '../services/api';
import type { ParserProfile, CreateParserProfilePayload } from '../types/timekeeping';

const FILE_TYPES = ['csv', 'txt', 'excel', 'xls', 'access_mdb', 'access_accdb'];

const FILE_TYPE_LABELS: Record<string, string> = {
  csv: 'CSV',
  txt: 'TXT',
  excel: 'Excel (xlsx)',
  xls: 'Excel (xls)',
  access_mdb: 'Access (mdb)',
  access_accdb: 'Access (accdb)',
};
const WORKER_TYPES = ['lambda', 'remote'];
const LAMBDA_MEMORIES = [128, 256, 512, 1024, 1536, 3008];

const emptyForm = (): CreateParserProfilePayload => ({
  name: '',
  fileType: 'csv',
  workerType: 'lambda',
  config: '{}',
  workerConfig: '{"memory":512,"timeout":300}',
});

interface ImportCandidate {
  id?: string;
  name: string;
  fileType: string;
  workerType: string;
  config: unknown;
  workerConfig: unknown;
}

interface ExistingItem {
  id: string;
  name: string;
  reason: string;
}

interface FailedItem {
  id: string;
  name: string;
  error: string;
}

const ParserProfiles: React.FC = () => {
  const dispatch = useAppDispatch();
  const [profiles, setProfiles] = useState<ParserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const navContainerRef = useRef<HTMLElement | null>(null);

  // Derived worker config fields
  const [lambdaMemory, setLambdaMemory] = useState(512);
  const [lambdaTimeout, setLambdaTimeout] = useState(300);

  // Import / Export state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [candidatesToImport, setCandidatesToImport] = useState<ImportCandidate[]>([]);
  const [alreadyExistingItems, setAlreadyExistingItems] = useState<ExistingItem[]>([]);
  const [importedItems, setImportedItems] = useState<Array<{ id: string; name: string }>>([]);
  const [failedItems, setFailedItems] = useState<FailedItem[]>([]);
  const [importStatus, setImportStatus] = useState<'preview' | 'importing' | 'completed'>('preview');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.listParserProfiles();
      setProfiles(res.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const syncWorkerConfig = (wt: string, mem: number, timeout: number) => {
    if (wt === 'lambda') {
      setForm(f => ({ ...f, workerConfig: JSON.stringify({ memory: mem, timeout }) }));
    } else {
      setForm(f => ({ ...f, workerConfig: '{}' }));
    }
  };

  const handleExport = () => {
    if (profiles.length === 0) {
      dispatch(showNotification({ message: 'No parser profiles to export', severity: 'warning' }));
      return;
    }

    const exportData = profiles.map((p) => {
      let parsedConfig: any = p.config;
      if (typeof p.config === 'string') {
        try {
          parsedConfig = JSON.parse(p.config);
        } catch {
          parsedConfig = p.config;
        }
      }
      let parsedWorkerConfig: any = p.workerConfig;
      if (typeof p.workerConfig === 'string') {
        try {
          parsedWorkerConfig = JSON.parse(p.workerConfig);
        } catch {
          parsedWorkerConfig = p.workerConfig;
        }
      }

      return {
        id: p.id,
        name: p.name,
        fileType: p.fileType,
        workerType: p.workerType,
        config: parsedConfig,
        workerConfig: parsedWorkerConfig,
      };
    });

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `parser-profiles-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    dispatch(
      showNotification({
        message: `Exported ${exportData.length} parser profile(s) successfully`,
        severity: 'success',
      })
    );
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const parsed = JSON.parse(text);
        const list: any[] = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed?.parserProfiles)
          ? parsed.parserProfiles
          : Array.isArray(parsed?.profiles)
          ? parsed.profiles
          : Array.isArray(parsed?.data)
          ? parsed.data
          : null;

        if (!list || list.length === 0) {
          dispatch(
            showNotification({
              message: 'Invalid JSON file: Expected a JSON array of parser profiles.',
              severity: 'error',
            })
          );
          return;
        }

        const existingById = new Map(profiles.map((p) => [p.id.toLowerCase(), p]));
        const existingByName = new Map(profiles.map((p) => [p.name.trim().toLowerCase(), p]));

        const toImport: ImportCandidate[] = [];
        const existing: ExistingItem[] = [];

        for (const raw of list) {
          if (!raw || typeof raw !== 'object') continue;
          const rawId = String(raw.id || raw.uuid || '').trim();
          const rawName = String(raw.name || '').trim();
          const rawFileType = String(raw.fileType || raw.file_type || '').trim().toLowerCase();
          const rawWorkerType = String(raw.workerType || raw.worker_type || 'lambda').trim().toLowerCase();

          if (!rawName) continue;

          if (rawId && existingById.has(rawId.toLowerCase())) {
            const match = existingById.get(rawId.toLowerCase())!;
            existing.push({
              id: rawId,
              name: rawName,
              reason: `UUID already exists (${match.name})`,
            });
            continue;
          }

          if (existingByName.has(rawName.toLowerCase())) {
            const match = existingByName.get(rawName.toLowerCase())!;
            existing.push({
              id: rawId || match.id,
              name: rawName,
              reason: `Profile with name "${rawName}" already exists`,
            });
            continue;
          }

          if (
            toImport.some(
              (c) =>
                (rawId && c.id && c.id.toLowerCase() === rawId.toLowerCase()) ||
                c.name.toLowerCase() === rawName.toLowerCase()
            )
          ) {
            existing.push({
              id: rawId || 'N/A',
              name: rawName,
              reason: 'Duplicate entry in imported file',
            });
            continue;
          }

          let configObj = raw.config;
          if (typeof configObj === 'string') {
            try {
              configObj = JSON.parse(configObj);
            } catch {
              /* keep original */
            }
          }
          if (!configObj || typeof configObj !== 'object') {
            configObj = {};
          }

          let workerConfigObj = raw.workerConfig || raw.worker_config;
          if (typeof workerConfigObj === 'string') {
            try {
              workerConfigObj = JSON.parse(workerConfigObj);
            } catch {
              /* keep original */
            }
          }
          if (!workerConfigObj || typeof workerConfigObj !== 'object') {
            workerConfigObj = rawWorkerType === 'lambda' ? { memory: 512, timeout: 300 } : {};
          }

          toImport.push({
            id: rawId || undefined,
            name: rawName,
            fileType: rawFileType || 'csv',
            workerType: rawWorkerType || 'lambda',
            config: configObj,
            workerConfig: workerConfigObj,
          });
        }

        setCandidatesToImport(toImport);
        setAlreadyExistingItems(existing);
        setImportedItems([]);
        setFailedItems([]);
        setImportStatus('preview');
        setImportDialogOpen(true);
      } catch (err: any) {
        dispatch(
          showNotification({
            message: 'Failed to parse JSON file: ' + (err?.message || 'Invalid JSON syntax'),
            severity: 'error',
          })
        );
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    reader.readAsText(file);
  };

  const handleExecuteImport = async () => {
    setImportStatus('importing');
    const successfullyImported: Array<{ id: string; name: string }> = [];
    const updatedAlreadyExisting: ExistingItem[] = [...alreadyExistingItems];
    const failed: FailedItem[] = [];

    for (const item of candidatesToImport) {
      try {
        const res = await apiService.createParserProfile({
          id: item.id,
          name: item.name,
          fileType: item.fileType,
          workerType: item.workerType,
          config: item.config,
          workerConfig: item.workerConfig,
        });
        successfullyImported.push({ id: res.data.id, name: res.data.name });
      } catch (err: any) {
        const errorMsg = err?.message || '';
        const isConflict =
          err?.status === 409 ||
          (err?.status === 400 && errorMsg.toLowerCase().includes('already exists')) ||
          errorMsg.toLowerCase().includes('already exists') ||
          errorMsg.toLowerCase().includes('conditionalcheckfailed');

        if (isConflict) {
          updatedAlreadyExisting.push({
            id: item.id || 'N/A',
            name: item.name,
            reason: errorMsg || 'Already exists in database',
          });
        } else {
          failed.push({
            id: item.id || 'N/A',
            name: item.name,
            error: errorMsg || 'Failed to create parser profile',
          });
        }
      }
    }

    setImportedItems(successfullyImported);
    setAlreadyExistingItems(updatedAlreadyExisting);
    setFailedItems(failed);
    setImportStatus('completed');

    if (successfullyImported.length > 0) {
      load();
      dispatch(
        showNotification({
          message: `Successfully imported ${successfullyImported.length} parser profile(s)`,
          severity: 'success',
        })
      );
    }
  };

  const openCreate = () => {
    setEditingId(null);
    const f = emptyForm();
    setForm(f);
    setLambdaMemory(512);
    setLambdaTimeout(300);
    setSaveError('');
    setDialogOpen(true);
  };

  const openEdit = (p: ParserProfile) => {
    setEditingId(p.id);
    let parsedWC: any = {};
    const wcString = typeof p.workerConfig === 'string' ? p.workerConfig : JSON.stringify(p.workerConfig);
    const cfgString = typeof p.config === 'string' ? p.config : JSON.stringify(p.config);
    try { parsedWC = JSON.parse(wcString); } catch { /* ignore */ }
    setForm({ name: p.name, fileType: p.fileType, workerType: p.workerType, config: cfgString, workerConfig: wcString });
    if (p.workerType === 'lambda') {
      setLambdaMemory(parsedWC.memory ?? 512);
      setLambdaTimeout(parsedWC.timeout ?? 300);
    }
    setSaveError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    // Sync workerConfig before save
    const finalForm = { ...form };
    if (form.workerType === 'lambda') {
      finalForm.workerConfig = JSON.stringify({ memory: lambdaMemory, timeout: lambdaTimeout });
    } else {
      finalForm.workerConfig = '{}';
    }
    try {
      const parsedConfig = JSON.parse(finalForm.config as string);
      const payload = {
        ...finalForm,
        workerConfig: JSON.parse(finalForm.workerConfig as string),
        config: parsedConfig,
      };
      if (editingId) {
        await apiService.updateParserProfile(editingId, payload);
      } else {
        await apiService.createParserProfile(payload);
      }
      setDialogOpen(false);
      load();
    } catch (e: any) {
      setSaveError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await apiService.deleteParserProfile(deleteId);
      setDeleteId(null);
      load();
    } catch (e: any) {
      setError(e.message || 'Delete failed');
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <Box p={3}>
        {/* Hidden file input for import */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelected}
          accept=".json,application/json"
          style={{ display: 'none' }}
        />

        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Parser Profiles</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExport}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileUpload />}
              onClick={() => fileInputRef.current?.click()}
            >
              Import
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              New Profile
            </Button>
          </Box>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>File Type</TableCell>
                  <TableCell>Worker Type</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No parser profiles yet.</TableCell>
                  </TableRow>
                ) : profiles.map(p => (
                  <TableRow key={p.id}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{FILE_TYPE_LABELS[p.fileType] ?? p.fileType}</TableCell>
                    <TableCell>{p.workerType}</TableCell>
                    <TableCell>{new Date(p.updatedAt).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(p)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteId(p.id)}><Delete fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Create / Edit dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ ref: navContainerRef }}
        >
          <FormNavigationProvider containerRef={navContainerRef}>
            <DialogTitle>{editingId ? 'Edit Parser Profile' : 'New Parser Profile'}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} mt={1}>
                {saveError && <Alert severity="error">{saveError}</Alert>}

                <NavTextField label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth required />

                <FormControl fullWidth>
                  <InputLabel>File Type</InputLabel>
                  <NavSelect value={form.fileType} label="File Type" onChange={e => setForm(f => ({ ...f, fileType: e.target.value as string }))}>
                    {FILE_TYPES.map(t => <MenuItem key={t} value={t}>{FILE_TYPE_LABELS[t] ?? t}</MenuItem>)}
                  </NavSelect>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Worker Type</InputLabel>
                  <NavSelect value={form.workerType} label="Worker Type" onChange={e => {
                    const wt = e.target.value as string;
                    setForm(f => ({ ...f, workerType: wt }));
                    syncWorkerConfig(wt, lambdaMemory, lambdaTimeout);
                  }}>
                    {WORKER_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </NavSelect>
                </FormControl>

                {/* Lambda worker config */}
                {form.workerType === 'lambda' && (
                  <>
                    <FormControl fullWidth>
                      <InputLabel>Memory (MB)</InputLabel>
                      <NavSelect value={lambdaMemory} label="Memory (MB)" onChange={e => {
                        const v = Number(e.target.value);
                        setLambdaMemory(v);
                        syncWorkerConfig('lambda', v, lambdaTimeout);
                      }}>
                        {LAMBDA_MEMORIES.map(m => <MenuItem key={m} value={m}>{m} MB</MenuItem>)}
                      </NavSelect>
                    </FormControl>
                    <NavTextField label="Timeout (seconds, 60–900)" type="number" navFieldType="number" value={lambdaTimeout}
                      onChange={e => {
                        const v = Number(e.target.value);
                        setLambdaTimeout(v);
                        syncWorkerConfig('lambda', lambdaMemory, v);
                      }} inputProps={{ min: 60, max: 900 }} fullWidth />
                  </>
                )}

                <NavTextField
                  label="Config (JSON)"
                  value={form.config}
                  onChange={e => setForm(f => ({ ...f, config: e.target.value }))}
                  multiline rows={4} fullWidth
                  helperText="Type-specific file parsing configuration"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={20} /> : (editingId ? 'Save' : 'Create')}
              </Button>
            </DialogActions>
          </FormNavigationProvider>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
          <DialogTitle>Delete Parser Profile?</DialogTitle>
          <DialogContent>This cannot be undone.</DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
              {deleting ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Import dialog */}
        <ImportParserProfilesDialog
          open={importDialogOpen}
          fileName={importFileName}
          candidates={candidatesToImport}
          existing={alreadyExistingItems}
          imported={importedItems}
          failed={failedItems}
          status={importStatus}
          onClose={() => setImportDialogOpen(false)}
          onImport={handleExecuteImport}
        />
      </Box>
    </DashboardLayout>
  );
};

// ─── Import Dialog ──────────────────────────────────────────────────────────

interface ImportParserProfilesDialogProps {
  open: boolean;
  fileName: string;
  candidates: ImportCandidate[];
  existing: ExistingItem[];
  imported: Array<{ id: string; name: string }>;
  failed: FailedItem[];
  status: 'preview' | 'importing' | 'completed';
  onClose: () => void;
  onImport: () => void;
}

const ImportParserProfilesDialog: React.FC<ImportParserProfilesDialogProps> = ({
  open,
  fileName,
  candidates,
  existing,
  imported,
  failed,
  status,
  onClose,
  onImport,
}) => {
  return (
    <Dialog
      open={open}
      onClose={() => status !== 'importing' && onClose()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FileUpload />
          <span>Import Parser Profiles</span>
        </Box>
        {fileName && (
          <Typography variant="caption" color="text.secondary">
            {fileName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {status === 'preview' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`Total in File: ${candidates.length + existing.length}`} size="small" />
              <Chip
                label={`Ready to Import: ${candidates.length}`}
                size="small"
                color={candidates.length > 0 ? 'success' : 'default'}
              />
              <Chip
                label={`Already Exists: ${existing.length}`}
                size="small"
                color={existing.length > 0 ? 'warning' : 'default'}
              />
            </Box>

            {existing.length > 0 && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  The following {existing.length} parser profile(s) already exist in the system and will be skipped:
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 160 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>UUID / ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {existing.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{item.id}</TableCell>
                          <TableCell>
                            <Chip label={item.reason} size="small" color="warning" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {candidates.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  New Parser Profiles to Import ({candidates.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>File Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Worker</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>UUID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {candidates.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell>
                            <Chip label={FILE_TYPE_LABELS[item.fileType] ?? item.fileType} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{item.workerType}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {item.id || 'Auto-generated'}
                          </TableCell>
                          <TableCell>
                            <Chip label="Ready to Import" size="small" color="success" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Alert severity="info">
                All parser profiles in this file already exist in the system. There are no new items to import.
              </Alert>
            )}
          </Box>
        )}

        {status === 'importing' && (
          <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={48} />
            <Typography variant="body1">Importing parser profiles, please wait...</Typography>
            <LinearProgress sx={{ width: '80%' }} />
          </Box>
        )}

        {status === 'completed' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {imported.length > 0 && (
              <Alert severity="success">
                <Typography variant="body2" fontWeight={600}>
                  Successfully imported {imported.length} parser profile(s):
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 160 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>UUID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {imported.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{item.id}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {existing.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" fontWeight={600}>
                  The following {existing.length} parser profile(s) already existed and were skipped:
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 160 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>UUID / ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {existing.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{item.id}</TableCell>
                          <TableCell>
                            <Chip label={item.reason} size="small" color="warning" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {failed.length > 0 && (
              <Alert severity="error">
                <Typography variant="body2" fontWeight={600}>
                  {failed.length} parser profile(s) failed to import:
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 140 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {failed.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell>{item.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {status === 'preview' && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={onImport}
              disabled={candidates.length === 0}
              startIcon={<FileUpload />}
            >
              Import {candidates.length > 0 ? `(${candidates.length})` : ''}
            </Button>
          </>
        )}
        {status === 'importing' && (
          <Button disabled>Importing...</Button>
        )}
        {status === 'completed' && (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ParserProfiles;
